<template>
  <AdminLogin
    v-if="!isAuthenticated"
    :is-loading="isAuthenticating"
    :error-message="authError"
    @submit="handleLogin"
  />

  <div v-else class="admin-layout" :class="{ 'light-theme': !isDark }">
    <!-- Backdrop sombre pour tiroir mobile -->
    <div
      v-if="isMobile && isMobileOpen"
      class="sidebar-backdrop"
      @click="isMobileOpen = false"
    ></div>

    <!-- Sidebar (Bureau ou Tiroir Mobile) -->
    <aside
      class="sidebar"
      :class="{ 'collapsed': isSidebarCollapsed && !isMobile, 'mobile-open': isMobileOpen }"
    >
      <div class="sidebar-logo">
        <div class="sidebar-logo-mark"><span>P</span></div>
        <div class="sidebar-logo-text">Pro<span>Fissa</span></div>
      </div>

      <nav class="sidebar-nav">
        <template v-for="(items, groupName) in groupedNavItems" :key="groupName">
          <div class="sidebar-group" :class="{ 'expanded': expandedGroups.includes(groupName) }">
            <div class="sidebar-section-label" @click="toggleGroup(groupName)" style="cursor: pointer; display: flex; justify-content: space-between; align-items: center;">
              <span>{{ groupName }}</span>
              <el-icon class="group-chevron" :style="{ transform: expandedGroups.includes(groupName) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }" v-if="!isSidebarCollapsed || isMobile">
                <CaretBottom />
              </el-icon>
            </div>
            <div class="group-items" v-show="expandedGroups.includes(groupName) || isSidebarCollapsed || isMobile">
              <div
                v-for="item in items"
                :key="item.id"
                class="nav-item"
                :class="{ active: activeNavId === item.id }"
                @click="navigate(item.path)"
              >
                <el-icon><component :is="item.icon" /></el-icon>
                <span class="nav-label">{{ item.label }}</span>
                <span
                  v-if="badges[item.id] > 0"
                  class="nav-badge"
                >{{ badges[item.id] > 99 ? '99+' : badges[item.id] }}</span>
              </div>
            </div>
          </div>
        </template>
      </nav>

      <div class="sidebar-footer">
        <div
          class="sidebar-user cursor-pointer hover:border-profissa-accent transition-colors"
          :class="{ '!border-profissa-accent shadow-glow': activeNavId === 'profile' }"
          title="Mon Compte (profil, mot de passe, permissions)"
          @click="navigate('/profile')"
        >
          <div class="sidebar-avatar">{{ userInitial }}</div>
          <div class="sidebar-user-info">
            <div class="sidebar-user-name">{{ currentUserName }}</div>
            <div class="sidebar-user-role">{{ currentUser?.role === 'support' ? 'Support' : 'Admin' }} Portal</div>
          </div>
          <button
            class="btn-logout"
            title="Se déconnecter"
            style="background:none;border:none;color:var(--text-dim);cursor:pointer;padding:5px;transition:color 0.2s;"
            @click.stop="logout"
          >
            <el-icon><SwitchButton /></el-icon>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main -->
    <main class="main-content">
      <header class="topbar">
        <div class="flex items-center gap-3 sm:gap-4">
          <el-button 
            @click="toggleSidebar" 
            circle 
            class="!bg-transparent !border-none text-xl hover:text-profissa-accent"
            :title="isMobile ? (isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu') : (isSidebarCollapsed ? 'Déplier le menu' : 'Réduire le menu')"
          >
            <el-icon><component :is="sidebarToggleIcon" /></el-icon>
          </el-button>
          <div class="topbar-title reveal">{{ currentPageTitle }}</div>
        </div>

        <div class="topbar-right flex items-center gap-3 sm:gap-4">
          <!-- Quick Search Button (Ctrl+K) -->
          <button
            @click="showSearchModal = true"
            class="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-profissa-glass-border hover:border-profissa-accent/50 text-xs text-profissa-text-muted hover:text-profissa-text-main transition-colors cursor-pointer"
            title="Recherche universelle (Ctrl+K)"
          >
            <el-icon class="text-sm text-profissa-accent"><Search /></el-icon>
            <span>Rechercher...</span>
            <kbd class="ml-1 px-1.5 py-0.5 text-[0.65rem] font-mono bg-white/10 rounded border border-white/10 text-profissa-accent font-bold">Ctrl K</kbd>
          </button>
          <el-button
            @click="showSearchModal = true"
            circle
            class="md:!hidden !bg-transparent !border-profissa-glass-border hover:border-profissa-accent"
            title="Recherche universelle"
          >
            <el-icon><Search /></el-icon>
          </el-button>

          <!-- Theme Toggle -->
          <el-button 
            @click="toggleTheme" 
            circle 
            class="!bg-transparent !border-profissa-glass-border hover:border-profissa-accent"
          >
            <el-icon><component :is="isDark ? Sunny : Moon" /></el-icon>
          </el-button>

          <div style="background:rgba(255,255,255,0.05);padding:6px 14px;border-radius:20px;border:1px solid var(--glass-border);display:flex;align-items:center;gap:8px;">
            <div style="width:8px;height:8px;background:var(--success);border-radius:50%;box-shadow:0 0 10px var(--success);"></div>
            <span style="font-size:0.8rem;color:var(--text-muted);font-weight:600;">{{ currentUserPhone }}</span>
          </div>
        </div>
      </header>
      <div class="page-body">
        <div class="reveal">
          <component
            :is="currentComponent"
            v-bind="currentViewProps"
            v-on="currentViewListeners"
            :key="currentViewKey"
          />
        </div>
      </div>
    </main>

    <!-- Modal Recherche Globale (Ctrl+K) -->
    <GlobalSearchModal
      v-model="showSearchModal"
      :current-user="currentUser"
      @navigate="navigate"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Fold, Expand, SwitchButton, CaretBottom, Sunny, Moon, Search } from '@element-plus/icons-vue'
import AdminLogin from './components/AdminLogin.vue'
import GlobalSearchModal from './components/shared/GlobalSearchModal.vue'
import { clearAdminToken, extractApiError, getAdminToken, setAdminToken } from './api'
import { fetchMe, loginWithPassword, fetchDashboardStats } from './services/adminApi'
import { defaultPathForUser, navItemById, navItemsForUser, userHasAnyPermission } from './navigation/adminNavigation'
import { ADMIN_PERMISSIONS_KEY } from './composables/useAdminPermissions'
import { formatPhone } from './utils/format'

const router = useRouter()
const route = useRoute()

const isAuthenticating = ref(false)
const authError = ref('')
const currentUser = ref(null)
const isAuthenticated = ref(false)
const showSearchModal = ref(false)
const badges = ref({ artisans: 0, disputes: 0, sav: 0, fraud: 0 })
let badgePollInterval = null

const isDark = ref(true)
const isSidebarCollapsed = ref(false)
const isMobile = ref(false)
const isMobileOpen = ref(false)

const updateDimensions = () => {
  isMobile.value = window.innerWidth < 1024
  if (!isMobile.value) {
    isMobileOpen.value = false
  }
}

const toggleSidebar = () => {
  if (isMobile.value) {
    isMobileOpen.value = !isMobileOpen.value
  } else {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
  }
}

const sidebarToggleIcon = computed(() => {
  if (isMobile.value) {
    return isMobileOpen.value ? Fold : Expand
  }
  return isSidebarCollapsed.value ? Expand : Fold
})

const expandedGroups = ref(['Opérations', 'Utilisateurs', 'Administration', 'Support & Séc.', 'Système'])

const toggleGroup = (groupName) => {
  if (expandedGroups.value.includes(groupName)) {
    expandedGroups.value = expandedGroups.value.filter(g => g !== groupName)
  } else {
    expandedGroups.value.push(groupName)
  }
}

const loadBadges = async () => {
  if (!userHasAnyPermission(currentUser.value?.permissions || [], ['dashboard.read'])) {
    badges.value = { artisans: 0, disputes: 0, sav: 0, fraud: 0 }
    return
  }
  try {
    const stats = await fetchDashboardStats()
    badges.value.artisans = stats.pending_artisans || 0
    badges.value.disputes = stats.open_disputes || 0
    badges.value.sav = stats.open_sav_tickets || 0
    badges.value.fraud = stats.open_fraud_alerts || 0
  } catch { /* badges are not critical */ }
}

const navItems = computed(() => {
  return navItemsForUser(currentUser.value)
})

const groupedNavItems = computed(() => {
  const groups = {}
  navItems.value.forEach(item => {
    const g = item.group || 'Principal'
    if (!groups[g]) groups[g] = []
    groups[g].push(item)
  })
  return groups
})
const currentMatchedRoute = computed(() => route.matched[route.matched.length - 1] || null)
const activeRouteMeta = computed(() => currentMatchedRoute.value?.meta || {})
const activeNavId = computed(() => activeRouteMeta.value.navId || 'login')
const activeNavItem = computed(() => navItemById(activeNavId.value))
const currentPageTitle = computed(() => activeRouteMeta.value.pageTitle || activeNavItem.value?.label || 'Connexion')
const currentComponent = computed(() => currentMatchedRoute.value?.components?.default || AdminLogin)
const currentViewProps = computed(() => {
  if (activeNavId.value !== 'chat') return {}
  return { initialInterventionId: route.params.interventionId || route.query.interventionId || '' }
})
const currentViewKey = computed(() => route.fullPath)
const currentViewListeners = computed(() => {
  if (activeNavId.value === 'artisans') {
    return { sanction: handleSanction, 'view-chat': handleViewChatFromArtisan }
  }
  if (activeNavId.value === 'missions') {
    return { 'view-chat': handleViewChatFromMission }
  }
  return {}
})
const currentUserPhone= computed(() => formatPhone(currentUser.value?.phone || ''))
const currentUserName = computed(() => currentUser.value?.full_name || formatPhone(currentUser.value?.phone) || 'Admin')
const userInitial     = computed(() => (currentUserName.value || 'A').charAt(0).toUpperCase())
const defaultPath = computed(() => defaultPathForUser(currentUser.value))
const providedPermissions = computed(() => currentUser.value?.permissions || [])

provide(ADMIN_PERMISSIONS_KEY, providedPermissions)

const navigate = (path) => {
  if (isMobileOpen.value) {
    isMobileOpen.value = false
  }
  if (route.path !== path) router.push(path)
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('light-theme', !isDark.value)
  document.documentElement.classList.toggle('dark', isDark.value)
  localStorage.setItem('admin-theme', isDark.value ? 'dark' : 'light')
}

const handleViewChatFromMission = (intervention) => {
  if (intervention?.id) router.push(`/chat/${intervention.id}`)
  else router.push('/chat')
}

const handleViewChatFromArtisan = () => {
  router.push('/chat')
}

const handleSanction = () => { router.push('/fraud') }

const ensureAllowedRoute = () => {
  const routePermissions = Array.isArray(activeRouteMeta.value.permissions) ? activeRouteMeta.value.permissions : []

  if (!isAuthenticated.value) {
    if (route.path !== '/login' && !isAuthenticating.value) router.replace('/login')
    return
  }

  if (route.path === '/login') {
    router.replace(defaultPath.value)
    return
  }

  if (!routePermissions.length) return

  const permissions = currentUser.value?.permissions || []
  if (!userHasAnyPermission(permissions, routePermissions)) {
    router.replace(defaultPath.value)
  }
}

const ensureAdminSession = async () => {
  try {
    const me = await fetchMe()
    if (!['admin', 'support'].includes(me?.role)) {
      clearAdminToken(); currentUser.value = null; isAuthenticated.value = false
      authError.value = 'Accès refusé : rôle admin ou support requis.'
      router.replace('/login')
      return
    }
    currentUser.value = me; isAuthenticated.value = true; authError.value = ''
    loadBadges()
    badgePollInterval = setInterval(loadBadges, 30000)
    ensureAllowedRoute()
  } catch (error) {
    clearAdminToken(); currentUser.value = null; isAuthenticated.value = false
    authError.value = extractApiError(error)
    router.replace('/login')
  }
}

const handleLogin = async ({ phone, password }) => {
  isAuthenticating.value = true
  try {
    const tokenPair = await loginWithPassword(phone, password)
    setAdminToken(tokenPair.access_token, tokenPair.refresh_token)
    await ensureAdminSession()
    loadBadges()
  } catch (error) {
    authError.value = extractApiError(error)
  } finally {
    isAuthenticating.value = false
  }
}

const logout = () => {
  clearInterval(badgePollInterval)
  badgePollInterval = null
  isMobileOpen.value = false
  badges.value = { artisans: 0, disputes: 0, sav: 0, fraud: 0 }
  clearAdminToken(); currentUser.value = null; isAuthenticated.value = false; authError.value = ''
  router.replace('/login')
}

const onSessionExpired = () => {
  currentUser.value = null
  isAuthenticated.value = false
  isMobileOpen.value = false
  authError.value = 'Session expirée. Veuillez vous reconnecter.'
  router.replace('/login')
}

onMounted(() => {
  const savedTheme = localStorage.getItem('admin-theme')
  if (savedTheme === 'light') {
    isDark.value = false
    document.documentElement.classList.add('light-theme')
  } else {
    document.documentElement.classList.add('dark')
  }

  window.addEventListener('resize', updateDimensions)
  updateDimensions()
  window.addEventListener('admin:session-expired', onSessionExpired)

  if (getAdminToken()) {
    isAuthenticating.value = true
    ensureAdminSession().finally(() => { isAuthenticating.value = false })
  } else {
    ensureAllowedRoute()
  }
})

onUnmounted(() => {
  clearInterval(badgePollInterval)
  window.removeEventListener('resize', updateDimensions)
  window.removeEventListener('admin:session-expired', onSessionExpired)
})

watch(() => route.fullPath, ensureAllowedRoute)
</script>

<style>
@import './assets/main.css';
</style>
